/***
 *
 *   PROFILE
 *   Update the user profile or close the account
 *
 **********/

import { Fragment, useContext, useEffect, useMemo, useState } from 'react';
import { AuthContext, ViewContext, Form, Card, AccountNav, useNavigate, Animate } from 'components/lib';
import { Modal, Box, Button } from '@mui/material';
import { UMButton } from '@urbanmetrix/um-kit';
import { getApiErrorMessage } from 'api/apiClient';
import {
  authClient,
  deleteOrganization,
  leaveOrganization,
  listOrganizations,
  verifyCurrentUserPassword,
} from '../../lib/auth-client';

type ProfileProps = Record<string, never>;

interface OrganizationSummary {
  id: string;
  name: string;
  [key: string]: unknown;
}

interface ProfileActionError {
  message?: string;
  code?: string;
  status?: number;
  error?:
    | {
        message?: string;
        code?: string;
      }
    | string;
}

function asProfileActionError(error: unknown): ProfileActionError {
  return (error ?? {}) as ProfileActionError;
}

export function Profile(_props: ProfileProps) {
  const navigate = useNavigate();

  // context
  const authContext = useContext(AuthContext);
  const viewContext = useContext(ViewContext);
  const [openOrganizationModal, setOpenOrganizationModal] = useState(false);
  const [openDeleteAccountModal, setOpenDeleteAccountModal] = useState(false);
  const [isSubmittingAction, setIsSubmittingAction] = useState(false);
  const [organizations, setOrganizations] = useState<OrganizationSummary[]>([]);
  const [deleteOrganizationAlsoAccount, setDeleteOrganizationAlsoAccount] = useState(false);
  const [deleteOrganizationPassword, setDeleteOrganizationPassword] = useState('');
  const [deleteOrganizationError, setDeleteOrganizationError] = useState('');
  const [deleteAccountPassword, setDeleteAccountPassword] = useState('');
  const [deleteAccountError, setDeleteAccountError] = useState('');

  useEffect(() => {
    let cancelled = false;

    async function loadOrganizations() {
      if (!authContext.isAuthenticated || !authContext.user?.session?.activeOrganizationId) {
        setOrganizations([]);
        return;
      }

      try {
        const nextOrganizations = await listOrganizations();
        if (!cancelled) {
          setOrganizations(Array.isArray(nextOrganizations) ? nextOrganizations : []);
        }
      } catch (error) {
        if (!cancelled) {
          setOrganizations([]);
        }
      }
    }

    loadOrganizations();

    return () => {
      cancelled = true;
    };
  }, [authContext.isAuthenticated, authContext.user?.session?.activeOrganizationId]);

  function getDeleteAccountErrorMessage(error: unknown, fallback = 'Unable to delete your account. Please try again.') {
    const err = asProfileActionError(error);
    const errorMessage = getApiErrorMessage(error, '');
    const errorCode = err?.code || (typeof err?.error === 'string' ? undefined : err?.error?.code) || err?.status;
    const normalizedMessage = typeof errorMessage === 'string' ? errorMessage.toLowerCase() : '';
    const isWrongPassword =
      errorCode === 400 ||
      errorCode === 'INVALID_PASSWORD' ||
      normalizedMessage.includes('bad request') ||
      normalizedMessage.includes('invalid password') ||
      normalizedMessage.includes('wrong password') ||
      normalizedMessage.includes('password');

    return isWrongPassword ? 'Wrong password' : errorMessage || fallback;
  }

  function closeOrganizationModal(force = false) {
    if (isSubmittingAction && !force) {
      return;
    }

    setOpenOrganizationModal(false);
    setDeleteOrganizationAlsoAccount(false);
    setDeleteOrganizationPassword('');
    setDeleteOrganizationError('');
  }

  async function handleOrganizationAction() {
    const organizationId = authContext.user?.session?.activeOrganizationId;
    const isOwner = authContext.permission?.owner;

    if (!organizationId) {
      return;
    }

    try {
      setIsSubmittingAction(true);
      setDeleteOrganizationError('');

      if (isOwner && deleteOrganizationAlsoAccount) {
        if (!deleteOrganizationPassword) {
          setDeleteOrganizationError('Please enter your password.');
          setIsSubmittingAction(false);
          return;
        }

        await verifyCurrentUserPassword(deleteOrganizationPassword);
      }

      if (isOwner) {
        await deleteOrganization(organizationId);
      } else {
        await leaveOrganization(organizationId);
      }

      closeOrganizationModal(true);

      if (isOwner && deleteOrganizationAlsoAccount) {
        await authClient.deleteUser({
          password: deleteOrganizationPassword,
          fetchOptions: {
            throw: true,
          },
        });

        try {
          await authClient.signOut();
        } catch (signOutError) {}
        window.location.href = '/login';
        return;
      }

      await authContext.refreshSession?.();
      navigate('/dashboard');
    } catch (error) {
      console.error(error);
      if (isOwner && deleteOrganizationAlsoAccount) {
        setDeleteOrganizationError(
          getDeleteAccountErrorMessage(error, 'Unable to delete your organization and account. Please try again.')
        );
      } else {
        viewContext.notification.show(
          getApiErrorMessage(error, 'An error occurred. Please try again.'),
          'error',
          true,
          'toast'
        );
      }
    } finally {
      setIsSubmittingAction(false);
    }
  }

  function closeDeleteAccountModal() {
    if (isSubmittingAction) {
      return;
    }

    setOpenDeleteAccountModal(false);
    setDeleteAccountPassword('');
    setDeleteAccountError('');
  }

  async function handleDeleteAccount() {
    if (!deleteAccountPassword) {
      setDeleteAccountError('Please enter your password.');
      return;
    }

    try {
      setIsSubmittingAction(true);
      setDeleteAccountError('');

      await authClient.deleteUser({
        password: deleteAccountPassword,
        fetchOptions: {
          throw: true,
        },
      });

      setOpenDeleteAccountModal(false);
      setDeleteAccountPassword('');
      setDeleteAccountError('');
      try {
        await authClient.signOut();
      } catch (signOutError) {}
      window.location.href = '/login';
    } catch (error) {
      console.error(error);
      setDeleteAccountError(getDeleteAccountErrorMessage(error));
    } finally {
      setIsSubmittingAction(false);
    }
  }

  const isOwner = authContext.permission?.owner;
  const activeOrganizationId = authContext.user?.session?.activeOrganizationId;
  const currentOrganization = useMemo(
    () => organizations.find((organization) => String(organization.id) === String(activeOrganizationId)),
    [activeOrganizationId, organizations]
  );
  const currentOrganizationName = currentOrganization?.name;
  const organizationTargetText = currentOrganizationName ? `"${currentOrganizationName}"` : 'your current organization';

  return (
    <Fragment>
      <AccountNav />
      <Animate>
        <Card title="Edit Your Profile" restrictWidth center extraClass="form-container" isStat={true}>
          {authContext.user?.email && <div style={{ marginBottom: '1.5rem' }}>{authContext.user.email}</div>}
          {authContext.user && (
            <Form
              buttonText="Save"
              buttonVariant="outlined"
              disableModalYesClass
              fullWidthButton={false}
              alignRight
              customSubmit={async (data) => {
                const { name } = data as { name: string };
                const result = await authClient.updateUser({
                  name,
                });

                if (result?.error) {
                  throw { response: { data: { message: result.error.message } } };
                }

                const nextUser = result?.data?.user || {
                  ...authContext.user,
                  name,
                };

                return {
                  message: 'Profile updated',
                  data: {
                    name: nextUser.name,
                    email: authContext.user?.email,
                  },
                };
              }}
              data={{
                name: {
                  label: 'Your Name',
                  type: 'text',
                  required: true,
                  value: authContext.user.name,
                  errorMessage: 'Please enter your name',
                },
              }}
              callback={(res) => {
                const { data } = res as { data: { data: { name?: string } } };
                authContext.update({
                  name: data.data.name,
                });
              }}
            />
          )}

          <div
            style={{
              marginTop: '1rem',
              display: 'flex',
              flexDirection: 'column',
              gap: '0.75rem',
              alignItems: 'flex-start',
            }}
          >
            {activeOrganizationId && !isOwner && (
              <UMButton
                textOnly
                onClick={() => {
                  setDeleteOrganizationAlsoAccount(false);
                  setDeleteOrganizationPassword('');
                  setDeleteOrganizationError('');
                  setOpenOrganizationModal(true);
                }}
                text="Leave Organization"
              />
            )}
            {/*
              // to-do apis delete-user not implemented on backend side
              <UMButton
                textOnly
                onClick={() => {
                  setDeleteAccountError('');
                  setDeleteAccountPassword('');
                  setOpenDeleteAccountModal(true);
                }}
                text="Delete Account"
              />
            */}
          </div>
        </Card>
      </Animate>

      <Modal className="custom-modal" open={openOrganizationModal} onClose={() => closeOrganizationModal()}>
        <Box className="organization-action-modal">
          <div className="organization-action-modal__header">
            <div className="organization-action-modal__title">
              {isOwner ? 'Delete Organization' : 'Leave Organization'}
            </div>
            <div className="organization-action-modal__subtitle">
              {isOwner
                ? `Are you sure you want to delete ${organizationTargetText}, which is your current organization? This action cannot be undone.`
                : `Are you sure you want to leave ${organizationTargetText}, which is your current organization?`}
            </div>
          </div>

          {isOwner && (
            <div className="organization-action-modal__field">
              {/*
                // to-do apis delete-user not implemented on backend side
                <label className="organization-action-modal__checkbox">
                <input
                  type="checkbox"
                  checked={deleteOrganizationAlsoAccount}
                  onChange={(event) => {
                    const isChecked = event.target.checked;
                    setDeleteOrganizationAlsoAccount(isChecked);
                    setDeleteOrganizationError('');
                    if (!isChecked) {
                      setDeleteOrganizationPassword('');
                    }
                  }}
                  disabled={isSubmittingAction}
                />
                <span>Delete also my account</span>
                </label>
              */}

              {deleteOrganizationAlsoAccount && (
                <div className="organization-action-modal__nested-field">
                  <label className="organization-action-modal__label" htmlFor="delete-organization-account-password">
                    Password
                  </label>
                  <input
                    id="delete-organization-account-password"
                    type="password"
                    className="organization-action-modal__input"
                    value={deleteOrganizationPassword}
                    onChange={(event) => {
                      setDeleteOrganizationPassword(event.target.value);
                      if (deleteOrganizationError) {
                        setDeleteOrganizationError('');
                      }
                    }}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter') {
                        handleOrganizationAction();
                      }
                    }}
                    disabled={isSubmittingAction}
                  />
                </div>
              )}

              {deleteOrganizationError && (
                <div className="organization-action-modal__error">{deleteOrganizationError}</div>
              )}
            </div>
          )}

          <div className="organization-action-modal__actions">
            <Button
              variant="contained"
              className="modal-no-btn organization-action-modal__cancel"
              onClick={() => closeOrganizationModal()}
              disabled={isSubmittingAction}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              className="modal-yes-btn organization-action-modal__confirm"
              onClick={handleOrganizationAction}
              disabled={isSubmittingAction}
            >
              {isOwner ? 'Delete' : 'Leave'}
            </Button>
          </div>
        </Box>
      </Modal>

      <Modal className="custom-modal" open={openDeleteAccountModal} onClose={closeDeleteAccountModal}>
        <Box className="organization-action-modal">
          <div className="organization-action-modal__header">
            <div className="organization-action-modal__title">Delete Account</div>
            <div className="organization-action-modal__subtitle">
              This will permanently delete your account. Enter your password to confirm.
            </div>
          </div>

          <div className="organization-action-modal__field">
            <label className="organization-action-modal__label" htmlFor="delete-account-password">
              Password
            </label>
            <input
              id="delete-account-password"
              type="password"
              className="organization-action-modal__input"
              value={deleteAccountPassword}
              onChange={(event) => {
                setDeleteAccountPassword(event.target.value);
                if (deleteAccountError) {
                  setDeleteAccountError('');
                }
              }}
              onKeyDown={(event) => {
                if (event.key === 'Enter') {
                  handleDeleteAccount();
                }
              }}
              disabled={isSubmittingAction}
            />
            {deleteAccountError && <div className="organization-action-modal__error">{deleteAccountError}</div>}
          </div>

          <div className="organization-action-modal__actions">
            <Button
              variant="contained"
              className="modal-no-btn organization-action-modal__cancel"
              onClick={closeDeleteAccountModal}
              disabled={isSubmittingAction}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              className="modal-yes-btn organization-action-modal__confirm"
              onClick={handleDeleteAccount}
              disabled={isSubmittingAction}
            >
              Delete Account
            </Button>
          </div>
        </Box>
      </Modal>
    </Fragment>
  );
}
